import React, { useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import { motion, useInView } from 'framer-motion';

import { NamedLink } from '../../components';
import Carousel from '../../components/Carousel';
import { getColumnCSS, getResponsiveImageSizes } from '../../components/Carousel/CarouselColumnHelper';
import { getCroppedImageStyle, getImageUrl } from '../../util/imageUtils';



/**
 * Animation variants for seller cards
 */
const containerVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      staggerChildren: 0.1,
      delayChildren: 0.2,
      ease: [0.25, 0.46, 0.45, 0.94], // Custom easing curve
    },
  },
};

const imageVariants = {
  hidden: {
    opacity: 0,
    scale: 0.8,
    rotateY: -10,
  },
  visible: {
    opacity: 1,
    scale: 1,
    rotateY: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

const textVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 },
  },
};

/**
 * Custom Cursor Component
 */
const CustomCursor = ({ x, y, isVisible, artistName }) => {
  return (
    <motion.div
      className="fixed pointer-events-none z-50"
      style={{ left: x, top: y }}
      initial={{ opacity: 0, scale: 0 }}
      animate={{
        opacity: isVisible ? 1 : 0,
        scale: isVisible ? 1 : 0,
      }}
      transition={{
        type: "spring",
        stiffness: 500,
        damping: 28,
      }}
    >
      {/* Cursor Icon - White with Black Outline and Rounded Edges */}
      <svg 
        width="24" 
        height="24" 
        viewBox="0 0 24 24" 
        fill="white"
        stroke="black"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="relative drop-shadow-sm"
      >
        <path d="M6 2L20.5 11.5L13 14L9 20L6 2Z"/>
      </svg>
      
      {/* Text Bubble - Positioned top right of cursor with more space */}
      <div 
        className="absolute text-black px-3 py-1 rounded-md shadow-lg border border-black font-medium text-sm whitespace-nowrap bg-white/70"
        style={{ 
          left: '25px',
          top: '-25px'
        }}
      >
        {artistName}
      </div>
    </motion.div>
  );
};

/**
 * Artist Profile Card Component - renders individual artist profile items with new design
 */
const ArtistProfileCard = ({ artist, responsiveImageSizes, options, index = 0, onHover, artistCategory }) => {
  const { id, username, name, featuredListings = [], whatYouSell, profileImage, publicPromoCode, locationCity } = artist;
  const ref = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  const isInView = useInView(ref, { margin: "-100px" });
  
  // Track if animation should trigger (only first time in view)
  const shouldAnimate = isInView && !hasAnimated;
  
  // Mark as animated when it comes into view for the first time
  useEffect(() => {
    if (isInView && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [isInView, hasAnimated]);
  
  // Ensure we have exactly 4 images, use first 4 if more exist
  const displayImages = featuredListings.slice(0, 4);
  const placeholderCount = Math.max(0, 4 - displayImages.length);
  
  const profileHref = username ? `/artist/${username}` : null;

  const cardContent = (
    <motion.div 
      ref={ref}
      className="flex flex-col bg-white/70 hover:bg-slate-50 hover:shadow-xl rounded-lg border border-black h-full p-3 transition-colors transition-shadow duration-300 ease-in-out group relative cursor-none"
      variants={containerVariants}
      initial="hidden"
      animate={hasAnimated ? "visible" : (shouldAnimate ? "visible" : "hidden")}
      onMouseEnter={() => onHover(name)}
      onMouseLeave={() => onHover(null)}
      style={{
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)"
      }}
    >
      {/* Sale Badge */}
      {publicPromoCode && (
        <div
          className="absolute top-4 left-4 bg-red-600 text-white px-4 py-2 rounded text-sm font-bold z-10"
        >
          {publicPromoCode.percentageOff}% Off
        </div>
      )}
      {/* 2x2 Grid of Featured Listing Images - Larger size */}
      <motion.div 
        className="grid grid-cols-2 gap-1.5 aspect-square overflow-hidden rounded-t-lg"
        variants={containerVariants}
      >
        {displayImages.map((listing, imgIndex) => {
          const { listingId, imageUrl, cropData } = listing;
          const imageUrlString = getImageUrl(imageUrl);
          
          return (
            <motion.div 
              key={listingId || imgIndex} 
              className="relative overflow-hidden outline outline-1 outline-gray-200 rounded-sm"
              variants={imageVariants}
            >
              <img 
                src={imageUrlString}
                alt={`Featured listing ${imgIndex + 1}`}
                className="w-full h-full object-cover group-hover:brightness-110 transition-all duration-300 ease-in-out"
                style={getCroppedImageStyle(cropData)}
                loading="lazy"
              />
            </motion.div>
          );
        })}
        {/* Placeholder divs if less than 4 images */}
        {Array.from({ length: placeholderCount }).map((_, imgIndex) => (
          <motion.div 
            key={`placeholder-${imgIndex}`} 
            className="bg-gray-200 flex items-center justify-center border border-gray-200"
            variants={imageVariants}
          >
            <div className="w-10 h-10 bg-gray-300 rounded opacity-50"></div>
          </motion.div>
        ))}
      </motion.div>

      {/* Artist Info - Increased padding and spacing */}
      <motion.div 
        className="pt-6 pb-2"
        variants={textVariants}
      >
        {/* Artist Name and Profile Image Row */}
        <div className="flex items-center space-x-3 px-4 py-1">
          {/* Profile Image - Larger size */}
          <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 border border-white shadow-lg border-2">
            {profileImage?.attributes?.variants?.['square-small']?.url ? (
              <img 
                src={profileImage.attributes.variants['square-small'].url}
                alt={`${name} profile`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                <div className="w-8 h-8 bg-gray-400 rounded-full"></div>
              </div>
            )}
          </div>
          
          {/* Artist Name and Info */}
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 font-['Be_Vietnam_Pro'] leading-tight m-0 p-0">
              {name}
            </h3>
            {/* What You Sell & Location - Judaica artists only */}
            {artistCategory === 'judaica' && (whatYouSell || locationCity) && (
              <p className="text-sm text-gray-500 m-0 p-0">
                {[whatYouSell, locationCity].filter(Boolean).join(', ')}
              </p>
            )}
          </div>
        </div>
      </motion.div>


    </motion.div>
  );

  return (
    <div className="block h-full" style={{ overflow: 'hidden' }}>
      {profileHref ? (
        <NamedLink name="PublicProfilePage" params={{ username }} className="block h-full">
          {cardContent}
        </NamedLink>
      ) : (
        cardContent
      )}
    </div>
  );
};

/**
 * Section component that shows featured artists in a carousel.
 * Now receives artist data as props instead of getting from Redux store.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.sectionId id of the section
 * @param {number?} props.numColumns number of columns to display (default: 4)
 * @param {string} props.artistCategory category of artists ('art' or 'judaica')
 * @param {Array} props.artists array of artists for this category
 * @param {boolean} props.inProgress whether data is currently loading
 * @param {Object} props.error error object if loading failed
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @returns {JSX.Element} Section for featured artists
 */
const FeaturedSellersCarousel = props => {
  const {
    sectionId,
    className,
    rootClassName,
    numColumns = 3,
    options,
    artistCategory, // 'art' or 'judaica'
    artists = [],
    inProgress = false,
    error = null,
  } = props;

  // Global cursor state
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [hoveredArtist, setHoveredArtist] = useState(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleArtistHover = (artistName) => {
    setHoveredArtist(artistName);
  };

  // Check data availability
  const hasArtists = artists.length > 0;
  const hasError = error;
  
  // Only show loading if we have no artists AND fetch is in progress
  // This prevents loading state during SSR hydration when data is already available
  const isLoading = !hasArtists && inProgress;

  // Handle loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px] p-8">
        <div className="text-lg text-gray-500 text-center">
          Loading featured artists...
        </div>
      </div>
    );
  }

  // Handle error state
  if (hasError) {
    return (
      <div className="flex items-center justify-center min-h-[200px] p-8">
        <div className="text-lg text-red-600 text-center">
          Unable to load featured artists
        </div>
      </div>
    );
  }

  // Don't render if no artists
  if (!hasArtists) {
    return null;
  }

  return (
    <div
      id={sectionId}
      className={classNames(className, rootClassName)}
    >
      <CustomCursor
        x={cursorPosition.x}
        y={cursorPosition.y}
        isVisible={!!hoveredArtist}
        artistName={hoveredArtist || ""}
      />
      
      <Carousel
        id={sectionId}
        numColumns={numColumns}
        className={getColumnCSS(numColumns)}
      >
        {artists.map((artist, index) => (
          <ArtistProfileCard
            key={artist.id || `artist-${index}`}
            artist={artist}
            responsiveImageSizes={getResponsiveImageSizes(numColumns)}
            options={options}
            index={index}
            onHover={handleArtistHover}
            artistCategory={artistCategory}
          />
        ))}
      </Carousel>
    </div>
  );
};

export default FeaturedSellersCarousel;
